<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50"
                :aria-hidden="!isOpen"
                aria-labelledby="modal-title"
                role="dialog"
            >
                <!-- Overlay -->
                <div
                    class="absolute inset-0 bg-black/50"
                    @click="$emit('close')"
                ></div>

                <!-- Desktop Modal -->
                <div
                    class="hidden md:flex absolute inset-0 items-center justify-center p-4"
                >
                    <div
                        class="flex flex-col w-[600px] rounded-lg bg-white dark:bg-slate-800 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)]"
                        @click.stop
                    >
                        <!-- Close button row -->
                        <div class="flex justify-end p-6 pb-0">
                            <button
                                @click="$emit('close')"
                                class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <Icon icon="x" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        <!-- Category Header -->
                        <div class="flex items-center justify-center gap-2">
                            <Icon
                                :icon="category?.icon || 'credit-card'"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                            <span
                                id="modal-title"
                                class="text-slate-500 dark:text-slate-400 text-base font-medium leading-6"
                            >
                                Best for {{ category?.name }}
                            </span>
                        </div>

                        <!-- Content -->
                        <div class="flex flex-col items-center px-6 pt-6">
                            <!-- Card Image -->
                            <div class="flex justify-center">
                                <div
                                    class="w-[300px] aspect-[1.586/1] rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
                                >
                                    <img
                                        v-if="cardImageUrl"
                                        :src="cardImageUrl"
                                        :alt="card?.card_title"
                                        class="w-full h-full object-cover"
                                    />
                                    <div
                                        v-else
                                        class="w-full h-full flex items-center justify-center"
                                    >
                                        <Icon
                                            icon="credit-card"
                                            class="w-16 h-16 text-slate-300 dark:text-slate-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Card Info Container -->
                            <div
                                class="flex flex-col items-center self-stretch pt-4 gap-4"
                            >
                                <div class="text-center">
                                    <h3
                                        class="text-slate-800 dark:text-slate-100 text-base font-normal leading-6"
                                    >
                                        {{ card?.card_title }}
                                        <span
                                            class="text-slate-500 dark:text-slate-400 font-normal"
                                        >
                                            ({{ card?.last_four }})
                                        </span>
                                    </h3>
                                    <div
                                        class="flex items-center justify-center gap-1.5 mt-0.5"
                                    >
                                        <p
                                            class="text-orange text-base font-medium leading-6"
                                        >
                                            {{ card?.multiplier_display }}
                                        </p>
                                        <img
                                            v-if="programImageUrl"
                                            :src="programImageUrl"
                                            :alt="card?.program_name"
                                            class="h-5 w-auto rounded-sm"
                                        />
                                    </div>
                                </div>

                                <!-- Bonus Description -->
                                <div
                                    v-if="bonusDescription"
                                    class="flex flex-col items-center max-w-[480px]"
                                >
                                    <p
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                                    >
                                        {{ bonusDescription }}
                                    </p>
                                </div>

                                <!-- Other Options -->
                                <div
                                    v-if="otherCards.length > 0"
                                    class="w-full pt-2 pb-10"
                                >
                                    <p
                                        class="text-slate-500 text-xs font-medium uppercase tracking-wide mb-3 text-center"
                                    >
                                        Other options
                                    </p>
                                    <div class="space-y-2">
                                        <div
                                            v-for="otherCard in otherCards"
                                            :key="
                                                otherCard.connected_account_id
                                            "
                                            class="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/50"
                                        >
                                            <div
                                                class="w-12 h-8 shrink-0 rounded overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center"
                                            >
                                                <img
                                                    v-if="
                                                        getOtherCardImageUrl(
                                                            otherCard
                                                        )
                                                    "
                                                    :src="
                                                        getOtherCardImageUrl(
                                                            otherCard
                                                        )
                                                    "
                                                    :alt="otherCard.card_title"
                                                    class="max-w-full max-h-full object-contain"
                                                />
                                                <Icon
                                                    v-else
                                                    icon="credit-card"
                                                    class="w-4 h-4 text-slate-300 dark:text-slate-500"
                                                />
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <UiTooltip
                                                    :text="
                                                        getOtherCardFullName(
                                                            otherCard
                                                        )
                                                    "
                                                    :interactive="false"
                                                    trigger-class="block min-w-0 max-w-full"
                                                    placement="top"
                                                >
                                                    <template #trigger>
                                                        <p
                                                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 truncate"
                                                        >
                                                            {{
                                                                otherCard.card_title
                                                            }}
                                                            <span
                                                                v-if="
                                                                    otherCard.last_four
                                                                "
                                                                class="text-slate-400"
                                                            >
                                                                ({{
                                                                    otherCard.last_four
                                                                }})
                                                            </span>
                                                        </p>
                                                    </template>
                                                </UiTooltip>
                                            </div>
                                            <div
                                                class="flex items-center gap-2 shrink-0"
                                            >
                                                <UiTooltip
                                                    :text="
                                                        otherCard.bonus_description ||
                                                        'No bonus description available'
                                                    "
                                                    :interactive="false"
                                                    trigger-class="inline-block"
                                                    placement="top"
                                                >
                                                    <template #trigger>
                                                        <span
                                                            class="text-orange text-sm font-medium"
                                                        >
                                                            {{
                                                                otherCard.multiplier_display
                                                            }}
                                                        </span>
                                                    </template>
                                                </UiTooltip>
                                                <button
                                                    type="button"
                                                    class="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                                                    :aria-label="`View ${otherCard.card_title} in My Wallet`"
                                                    @click="
                                                        $emit(
                                                            'view-other-in-wallet',
                                                            otherCard.connected_account_id
                                                        )
                                                    "
                                                    title="View in wallet"
                                                >
                                                    <Icon
                                                        icon="chevron-right-small"
                                                        class="w-5 h-5 text-slate-500"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="pb-12"></div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div
                            class="flex w-full justify-end items-center gap-2 px-6 pb-6"
                        >
                            <div class="relative mr-auto">
                                <div
                                    class="flex items-center gap-2 text-slate-800 dark:text-slate-200 cursor-default"
                                    @mouseenter="showTooltip = true"
                                    @mouseleave="showTooltip = false"
                                >
                                    <Icon
                                        icon="card-check"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-200"
                                    />
                                    <span class="text-sm font-medium leading-5">
                                        Why this card?
                                    </span>
                                </div>
                                <div
                                    v-show="showTooltip"
                                    :class="tooltipClasses"
                                >
                                    <template v-if="card?.is_fallback">
                                        No cards in your wallet have a bonus for
                                        {{ category?.name }}. This card has the
                                        best base earning rate.
                                    </template>
                                    <template v-else>
                                        Based on your wallet, this card offers
                                        the best
                                        {{ card?.multiplier_type }} earning rate
                                        for {{ category?.name }}.
                                    </template>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                @click="$emit('view-in-wallet')"
                            >
                                View in My Wallet
                                <Icon icon="chevron-right" class="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Mobile Fullscreen Modal -->
                <div
                    class="md:hidden absolute inset-0 bg-white dark:bg-slate-800 flex flex-col animate-slide-up"
                    @click.stop
                >
                    <!-- Close button (top right) -->
                    <div class="flex justify-end p-4">
                        <button
                            @click="$emit('close')"
                            class="w-10 h-10 flex items-center justify-center rounded-lg active:bg-slate-100 dark:active:bg-slate-700"
                        >
                            <Icon icon="x" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        </button>
                    </div>

                    <!-- Content -->
                    <div class="flex-1 overflow-y-auto px-12">
                        <!-- Category Header (centered) -->
                        <div
                            class="flex items-center justify-center gap-2 mb-6"
                        >
                            <Icon
                                :icon="category?.icon || 'credit-card'"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                            <span class="text-slate-500 dark:text-slate-400 text-base font-medium">
                                Best for {{ category?.name }}
                            </span>
                        </div>

                        <!-- Card Image (48px margins, max 320px) -->
                        <div class="flex justify-center mb-6">
                            <div
                                class="w-full max-w-[320px] aspect-[1.586/1] rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
                            >
                                <img
                                    v-if="cardImageUrl"
                                    :src="cardImageUrl"
                                    :alt="card?.card_title"
                                    class="w-full h-full object-cover"
                                />
                                <div
                                    v-else
                                    class="w-full h-full flex items-center justify-center"
                                >
                                    <Icon
                                        icon="credit-card"
                                        class="w-12 h-12 text-slate-300 dark:text-slate-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Card Info -->
                        <div class="text-center mb-4">
                            <h3
                                class="text-slate-800 dark:text-slate-100 text-xl font-normal leading-7"
                            >
                                {{ card?.card_title }}
                                <span class="text-slate-500 dark:text-slate-400 font-normal">
                                    ({{ card?.last_four }})
                                </span>
                            </h3>
                            <div
                                class="flex items-center justify-center gap-1.5 mt-1"
                            >
                                <p class="text-orange text-lg font-medium">
                                    {{ card?.multiplier_display }}
                                </p>
                                <img
                                    v-if="programImageUrl"
                                    :src="programImageUrl"
                                    :alt="card?.program_name"
                                    class="h-5 w-auto rounded-sm"
                                />
                            </div>
                        </div>

                        <!-- Bonus Description -->
                        <p
                            v-if="bonusDescription"
                            class="text-slate-500 dark:text-slate-400 text-sm text-center leading-relaxed mb-6"
                        >
                            {{ bonusDescription }}
                        </p>
                        <!-- Other Options -->
                        <div v-if="otherCards.length > 0" class="w-full mb-6">
                            <p
                                class="text-slate-500 text-xs font-medium uppercase tracking-wide mb-3 text-center"
                            >
                                Other options
                            </p>
                            <div class="space-y-2">
                                <div
                                    v-for="otherCard in otherCards"
                                    :key="otherCard.connected_account_id"
                                    class="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700/50"
                                >
                                    <div
                                        class="w-12 h-8 shrink-0 rounded overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center"
                                    >
                                        <img
                                            v-if="
                                                getOtherCardImageUrl(otherCard)
                                            "
                                            :src="
                                                getOtherCardImageUrl(otherCard)
                                            "
                                            :alt="otherCard.card_title"
                                            class="max-w-full max-h-full object-contain"
                                        />
                                        <Icon
                                            v-else
                                            icon="credit-card"
                                            class="w-4 h-4 text-slate-300 dark:text-slate-500"
                                        />
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <UiTooltip
                                            :text="
                                                getOtherCardFullName(otherCard)
                                            "
                                            :interactive="false"
                                            trigger-class="block min-w-0 max-w-full"
                                            placement="top"
                                        >
                                            <template #trigger>
                                                <p
                                                    class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 truncate"
                                                >
                                                    {{ otherCard.card_title }}
                                                    <span
                                                        v-if="
                                                            otherCard.last_four
                                                        "
                                                        class="text-slate-400"
                                                    >
                                                        ({{
                                                            otherCard.last_four
                                                        }})
                                                    </span>
                                                </p>
                                            </template>
                                        </UiTooltip>
                                    </div>
                                    <div
                                        class="flex items-center gap-2 shrink-0"
                                    >
                                        <UiTooltip
                                            :text="
                                                otherCard.bonus_description ||
                                                'No bonus description available'
                                            "
                                            :interactive="false"
                                            trigger-class="inline-block"
                                            placement="top"
                                        >
                                            <template #trigger>
                                                <span
                                                    class="text-orange text-sm font-medium"
                                                >
                                                    {{
                                                        otherCard.multiplier_display
                                                    }}
                                                </span>
                                            </template>
                                        </UiTooltip>
                                        <button
                                            type="button"
                                            class="w-8 h-8 flex items-center justify-center rounded active:bg-slate-100 dark:active:bg-slate-600 transition-colors"
                                            :aria-label="`View ${otherCard.card_title} in My Wallet`"
                                            @click="
                                                $emit(
                                                    'view-other-in-wallet',
                                                    otherCard.connected_account_id
                                                )
                                            "
                                        >
                                            <Icon
                                                icon="chevron-right-small"
                                                class="w-5 h-5 text-slate-500"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Why this card? (centered with tooltip) -->
                        <div class="relative flex flex-col items-center z-10">
                            <!-- Tooltip (appears above button) -->
                            <Transition name="fade">
                                <div
                                    v-if="showMobileWhyTooltip"
                                    class="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex flex-col items-start w-[191px] px-2 py-1 rounded-lg bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] z-20"
                                >
                                    <span class="text-white text-sm">
                                        <template v-if="card?.is_fallback">
                                            Best base earning rate in your
                                            wallet
                                        </template>
                                        <template v-else>
                                            Highest {{ card?.multiplier_type }}
                                            rate in this category
                                        </template>
                                    </span>
                                    <!-- Arrow -->
                                    <div
                                        class="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800"
                                    ></div>
                                </div>
                            </Transition>

                            <button
                                type="button"
                                class="why-card-btn relative z-20 flex items-center gap-2 text-slate-800 dark:text-slate-200 active:text-slate-600 dark:active:text-slate-400 transition-colors min-h-[44px]"
                                @click.stop="
                                    showMobileWhyTooltip = !showMobileWhyTooltip
                                "
                            >
                                <Icon icon="card-check" class="w-5 h-5" />
                                <span class="text-sm font-medium">
                                    Why this card?
                                </span>
                            </button>
                        </div>

                        <!-- Click outside overlay to close tooltip -->
                        <div
                            v-if="showMobileWhyTooltip"
                            class="fixed inset-0 z-0"
                            @click="showMobileWhyTooltip = false"
                        ></div>
                    </div>

                    <!-- Footer -->
                    <div
                        class="flex w-full justify-center items-end gap-2 pt-4 px-6 pb-12"
                    >
                        <button
                            type="button"
                            class="flex flex-1 items-center justify-center gap-2 py-2.5 px-4 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] active:bg-slate-50 dark:active:bg-slate-700 transition-colors"
                            @click="$emit('view-in-wallet')"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                View in My Wallet
                            </span>
                            <Icon
                                icon="chevron-right-small"
                                class="w-5 h-5 text-slate-800 dark:text-slate-100"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';

const showTooltip = ref(false);
const showMobileWhyTooltip = ref(false);

const tooltipClasses =
    'absolute bottom-full left-0 mb-2 py-2 px-3 text-sm text-white bg-[#1E293B] rounded-lg shadow-sm z-50 w-[200px]';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    card: {
        type: Object,
        default: null,
    },
    category: {
        type: Object,
        default: null,
    },
    otherCards: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits([
    'close',
    'view-in-wallet',
    'view-other-in-wallet',
    'why-this-card',
]);

const cdnUrl = computed(() => window.cdnUrl);

const cardImageUrl = computed(() => {
    const img = props.card?.card_image;
    if (img?.path && img?.file_name) {
        return `${cdnUrl.value}/${img.path}/_small/${img.file_name}`;
    }
    return null;
});

const programImageUrl = computed(() => {
    const img = props.card?.program_image;
    if (img?.path && img?.file_name) {
        return `${cdnUrl.value}/${img.path}/_small/${img.file_name}`;
    }
    return null;
});

const bonusDescription = computed(() => {
    return props.card?.bonus_description || null;
});

const getOtherCardImageUrl = (otherCard) => {
    const img = otherCard?.card_image;
    if (img?.path && img?.file_name) {
        return `${cdnUrl.value}/${img.path}/_small/${img.file_name}`;
    }
    return null;
};

const getOtherCardFullName = (otherCard) => {
    if (!otherCard?.card_title) return '';
    return otherCard.last_four
        ? `${otherCard.card_title} (${otherCard.last_four})`
        : otherCard.card_title;
};

const handleKeydown = (event) => {
    if (event.key === 'Escape' && props.isOpen) {
        emit('close');
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes slide-up {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}
.animate-slide-up {
    animation: slide-up 0.3s ease-out;
}

.why-card-btn {
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
}

.why-card-btn:focus,
.why-card-btn:active,
.why-card-btn:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
}
</style>
